import React, { useState, useEffect, useRef, useContext } from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Box, 
  Typography, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon,
  Chip,
  Divider,
  Collapse,
  Switch
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Dashboard, 
  Person, 
  Inventory, 
  Description, 
  ContactSupport, 
  Settings, 
  AccountCircle,
  CreditCard,
  Star,
  WorkspacePremium,
  Logout,
  Business,
  People,
  CalendarMonth,
  // COMMENTED OUT - Public Profile icons - temporarily disabled
  // Public as PublicIcon,
  // Visibility as PreviewIcon,
  // Collections as PortfolioIcon,
  // Email as InquiriesIcon,
  // Settings as ProfileSettingsIcon,
  ExpandLess,
  ExpandMore,
  Link as LinkIcon,
  QuestionAnswer as InquiryIcon,
  Gavel as RecordPacketIcon,
  DarkMode as DarkModeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { UseApi } from './Hooks/UseApi';
import { UseAuthRoles } from './Hooks/UseAuthRoles';
import { ThemeContext } from './ThemeContext';

const NavBar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  // COMMENTED OUT - Public Profile state - temporarily disabled
  // const [publicProfileOpen, setPublicProfileOpen] = useState(false);
  const [inquiryFormsOpen, setInquiryFormsOpen] = useState(false);
  const [user, setUser] = useState({ first_name: '', last_name: '' });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const api = UseApi();
  const { logout } = useAuth0();
  const { darkMode, toggleDarkMode } = useContext(ThemeContext);
  const { 
    getSubscriptionLevel, 
    isStudioOwner,
    // COMMENTED OUT - temporarily disabled
    // hasPublicProfileAccess,
    hasStudioAccess
  } = UseAuthRoles();

  // Use refs to prevent excessive API calls and handle component unmounting
  const dataLoaded = useRef(false);
  const isMounted = useRef(true);

  // Get current subscription tier
  const currentTier = getSubscriptionLevel();
  
  // Check if user can access Record Packets (Growth Studio owners only)
  const canAccessRecordPackets = currentTier === 'growth_studio' && isStudioOwner();

  useEffect(() => {
    // Cleanup when component unmounts
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Only fetch if we haven't loaded the data already
    if (!dataLoaded.current) {
      fetchUserData();
    }
  }, []);

  const fetchUserData = async () => {
    try {
      const data = await api.fetchWithAuth('/user/profile/');
      
      if (!isMounted.current) return;
      
      if (data) {
        setUser({
          first_name: data.first_name || '',
          last_name: data.last_name || ''
        });
        
        // Store user data in localStorage for other components
        localStorage.setItem('userData', JSON.stringify(data));
      }
      
      dataLoaded.current = true;
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // Listen for profile updates through localStorage
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'userData') {
        try {
          const updatedUser = JSON.parse(event.newValue);
          if (updatedUser && isMounted.current) {
            setUser({
              first_name: updatedUser.first_name || '',
              last_name: updatedUser.last_name || ''
            });
          }
        } catch (error) {
          console.error("Error parsing user data from localStorage:", error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleLogout = () => {
    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('authToken');
    
    // Clear any other auth-related storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Use Auth0's logout function
    logout({
      logoutParams: {
        returnTo: window.location.origin // Fixed: redirect to home page instead of /login
      }
    });
  };

  // Handle navigation and close drawer
  const handleNavigation = (route) => {
    navigate(route);
    setDrawerOpen(false);
  };

  // COMMENTED OUT - Public Profile submenu toggle - temporarily disabled
  // const handlePublicProfileToggle = (event) => {
  //   event.stopPropagation();
  //   setPublicProfileOpen(!publicProfileOpen);
  // };

  // Handle inquiry forms submenu toggle
  const handleInquiryFormsToggle = (event) => {
    event.stopPropagation();
    setInquiryFormsOpen(!inquiryFormsOpen);
  };

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, route: '/dashboard' },
    { text: 'Clients', icon: <Person />, route: '/clients' },
    { text: 'Appointments', icon: <CalendarMonth />, route: '/appointments' },
    { text: 'Consent Forms', icon: <Description />, route: '/consent-forms' },
    { text: 'Inventory', icon: <Inventory />, route: '/inventory' },
    { text: 'Support', icon: <ContactSupport />, route: '/support' },
  ];

  // Inquiry Forms submenu items (All Tiers)
  const inquiryFormItems = [
    { 
      text: 'Tattoo Inquiries', 
      icon: <InquiryIcon />, 
      route: '/tattoo-inquiries',
      description: 'View & manage client requests'
    },
    { 
      text: 'Form Settings', 
      icon: <LinkIcon />, 
      route: '/inquiry-form-settings',
      description: 'Configure your shareable form'
    }
  ];

  // COMMENTED OUT - Public Profile submenu items (Growth Studio only) - temporarily disabled
  // const publicProfileItems = [
  //   { 
  //     text: 'Profile Settings', 
  //     icon: <ProfileSettingsIcon />, 
  //     route: '/public-profile-settings',
  //     description: 'Configure your public profile'
  //   },
  //   { 
  //     text: 'Portfolio Manager', 
  //     icon: <PortfolioIcon />, 
  //     route: '/portfolio-manager',
  //     description: 'Manage your work portfolio'
  //   },
  //   { 
  //     text: 'Contact Inquiries', 
  //     icon: <InquiriesIcon />, 
  //     route: '/contact-inquiries',
  //     description: 'View client inquiries'
  //   },
  //   { 
  //     text: 'Profile Preview', 
  //     icon: <PreviewIcon />, 
  //     route: '/public-profile-preview',
  //     description: 'Preview your public profile'
  //   }
  // ];

  // Get subscription tier styling - FIXED VERSION
  const getSubscriptionChip = () => {
    console.log('Current tier for chip:', currentTier); // Debug log
    
    if (currentTier === 'unpaid') {
      return (
        <Chip 
          label="Free" 
          size="small" 
          variant="outlined"
          sx={{ 
            ml: 1, 
            color: 'white', 
            borderColor: 'rgba(255, 255, 255, 0.3)',
            fontSize: '0.75rem'
          }} 
        />
      );
    }

    // Define all tier configurations
    const tierConfig = {
      basic: { 
        label: 'Basic', 
        color: '#4caf50',
        icon: <Star sx={{ fontSize: 14, mr: 0.5 }} />
      },
      standard: { 
        label: 'Standard', 
        color: '#2196f3',
        icon: <WorkspacePremium sx={{ fontSize: 14, mr: 0.5 }} />
      },
      starter_studio: { 
        label: 'Starter Studio', 
        color: '#ff9800',
        icon: <Business sx={{ fontSize: 14, mr: 0.5 }} />
      },
      growth_studio: { 
        label: 'Growth Studio', 
        color: '#9c27b0',
        icon: <WorkspacePremium sx={{ fontSize: 14, mr: 0.5 }} />
      }
    };

    // Get config for current tier
    const config = tierConfig[currentTier];
    
    // If no config found, show unknown
    if (!config) {
      console.warn('No config found for tier:', currentTier);
      return (
        <Chip 
          label="Unknown" 
          size="small" 
          variant="outlined"
          sx={{ 
            ml: 1, 
            color: 'white', 
            borderColor: 'rgba(255, 255, 255, 0.3)',
            fontSize: '0.75rem'
          }} 
        />
      );
    }
    
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        size="small"
        sx={{
          ml: 1,
          backgroundColor: config.color,
          color: 'white',
          fontSize: '0.75rem',
          '& .MuiChip-icon': {
            color: 'white'
          }
        }}
      />
    );
  };

  // Get plan display name for the drawer
  const getPlanDisplayName = () => {
    const planNames = {
      'unpaid': 'Free Plan',
      'basic': 'Basic Plan',
      'standard': 'Standard Plan',
      'starter_studio': 'Starter Studio',
      'growth_studio': 'Growth Studio'
    };
    return planNames[currentTier] || 'Unknown Plan';
  };

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: '#222', mb: 3, overflow: 'hidden' }}>
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              sx={{ 
                ml: { xs: 1, sm: 2 }, 
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                cursor: 'pointer',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                whiteSpace: 'nowrap'
              }}
              onClick={() => navigate('/dashboard')}
            >
              InkedUpManager
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexShrink: 1 }}>
            {/* User name and chip - hidden on mobile */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: 1 }}>
              <AccountCircle sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
                {loading 
                  ? 'Loading...' 
                  : user.first_name 
                    ? `${user.first_name} ${user.last_name}`.trim() 
                    : 'Guest User'}
              </Typography>
              {!loading && getSubscriptionChip()}
            </Box>
            {/* Icons always visible */}
            <IconButton sx={{ ml: { xs: 0, sm: 1 } }} color="inherit" onClick={() => navigate('/settings')}>
              <Settings />
            </IconButton>
            <IconButton 
              sx={{ ml: { xs: 0, sm: 1 } }} 
              color="inherit" 
              onClick={handleLogout}
              title="Logout"
            >
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Navigation Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 280 }} role="presentation">
          <List>
            {/* Main Menu Items */}
            {menuItems.map((item, index) => (
              <ListItem 
                button 
                key={index} 
                onClick={() => handleNavigation(item.route)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
            
            <Divider sx={{ my: 1 }} />

            {/* Record Packets - Growth Studio Owners Only */}
            {canAccessRecordPackets && (
              <>
                <ListItem button onClick={() => handleNavigation('/record-packets')}>
                  <ListItemIcon>
                    <RecordPacketIcon sx={{ color: '#9c27b0' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Record Packets" 
                    secondary="Liability documentation"
                    primaryTypographyProps={{
                      sx: { color: '#9c27b0', fontWeight: 'bold' }
                    }}
                  />
                </ListItem>
                <Divider sx={{ my: 1 }} />
              </>
            )}

            {/* Inquiry Forms Section - Available for ALL Tiers */}
            <ListItem button onClick={handleInquiryFormsToggle}>
              <ListItemIcon>
                <InquiryIcon sx={{ color: '#2196f3' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Inquiry Forms" 
                secondary="Shareable booking requests"
                primaryTypographyProps={{
                  sx: { color: '#2196f3', fontWeight: 'bold' }
                }}
              />
              {inquiryFormsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            
            <Collapse in={inquiryFormsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {inquiryFormItems.map((item, index) => (
                  <ListItem 
                    button 
                    key={index} 
                    onClick={() => handleNavigation(item.route)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      secondary={item.description}
                      primaryTypographyProps={{ 
                        variant: 'body2',
                        sx: { fontWeight: 500 }
                      }}
                      secondaryTypographyProps={{ 
                        variant: 'caption',
                        sx: { fontSize: '0.7rem' }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>
            
            <Divider sx={{ my: 1 }} />
            
            {/* COMMENTED OUT - Public Profile Section - Growth Studio Only - temporarily disabled
            {hasPublicProfileAccess() && (
              <>
                <ListItem button onClick={handlePublicProfileToggle}>
                  <ListItemIcon>
                    <PublicIcon sx={{ color: '#9c27b0' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Public Profile" 
                    secondary="Manage your online presence"
                    primaryTypographyProps={{
                      sx: { color: '#9c27b0', fontWeight: 'bold' }
                    }}
                  />
                  {publicProfileOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                
                <Collapse in={publicProfileOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {publicProfileItems.map((item, index) => (
                      <ListItem 
                        button 
                        key={index} 
                        onClick={() => handleNavigation(item.route)}
                        sx={{ pl: 4 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.text}
                          secondary={item.description}
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { fontWeight: 500 }
                          }}
                          secondaryTypographyProps={{ 
                            variant: 'caption',
                            sx: { fontSize: '0.7rem' }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
                
                <Divider sx={{ my: 1 }} />
              </>
            )}
            */}
            
            {/* Team Management Section - Only for studio users */}
            {hasStudioAccess() && (
              <>
                <ListItem button onClick={() => handleNavigation('/studio-management')}>
                  <ListItemIcon>
                    <People />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Team Management" 
                    secondary="Manage studio members"
                  />
                </ListItem>
                <Divider sx={{ my: 1 }} />
              </>
            )}
            
            {/* Subscription Management Section */}
            <ListItem button onClick={() => handleNavigation('/subscription-manager')}>
              <ListItemIcon>
                <CreditCard />
              </ListItemIcon>
              <ListItemText 
                primary="Subscription" 
                secondary={currentTier === 'unpaid' ? 
                'Upgrade available' : getPlanDisplayName()}
              />
            </ListItem>
            
            {currentTier === 'unpaid' && (
              <ListItem button onClick={() => handleNavigation('/subscription')} sx={{ pl: 4 }}>
                <ListItemIcon>
                  <Star sx={{ color: '#4caf50' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Choose Plan" 
                  primaryTypographyProps={{ 
                    variant: 'body2',
                    sx: { color: '#4caf50', fontWeight: 'bold' }
                  }}
                />
              </ListItem>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Dark Mode Toggle */}
            <ListItem>
              <ListItemIcon>
                <DarkModeIcon />
              </ListItemIcon>
              <ListItemText primary="Dark Mode" />
              <Switch
                edge="end"
                checked={darkMode}
                onChange={toggleDarkMode}
                size="small"
              />
            </ListItem>
            
            <Divider sx={{ my: 1 }} />
            
            {/* Logout Option in Drawer */}
            <ListItem button onClick={handleLogout}>
              <ListItemIcon>
                <Logout />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default NavBar;